import type { AssessmentOverview, OverviewSection, PrerequisiteGate } from '@assessments/shared'
import { SectionCompletionState } from '@assessments/shared'
import { ArrowLeftIcon, CheckIcon, FolderIcon, LayoutIcon } from '@/components/ui/icons'
import { cn } from '@/lib/utils'
import { SECTION_STATE_LABEL, SECTION_STATE_MARK } from './sectionState.js'

/**
 * Completion mark beside each sidebar entry. The check is a glyph, not text — white on
 * the brand green is 3.20:1, which clears the non-text floor but not AA body — so the
 * state is carried for assistive technology by the label rather than the shape.
 */
function Mark({ state }: { state: SectionCompletionState }) {
  const { className, checked } = SECTION_STATE_MARK[state]
  return (
    <span className={cn('grid h-7 w-7 shrink-0 place-items-center rounded-full', className)}>
      {checked && <CheckIcon className="text-success-on" />}
      <span className="sr-only">{SECTION_STATE_LABEL[state]}</span>
    </span>
  )
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-6 pt-6 pb-3 text-[11px] font-semibold tracking-[0.14em] text-inverse/55 uppercase">
      {children}
    </div>
  )
}

function EntryRow({
  title,
  mark,
  onClick,
  disabled,
  active,
}: {
  title: string
  mark: SectionCompletionState
  onClick?: () => void
  disabled?: boolean
  /** The section/prerequisite currently open — rendered as a highlighted location, not a control. */
  active?: boolean
}) {
  const content = (
    <>
      <Mark state={mark} />
      <span className="min-w-0 text-[15px] leading-snug text-inverse">{title}</span>
    </>
  )
  if (active) {
    return (
      <div
        aria-current="page"
        className="mx-3 flex items-start gap-3 rounded-card bg-sidebar-raised px-3 py-3"
      >
        {content}
      </div>
    )
  }
  if (!onClick || disabled) {
    return (
      <div className={cn('flex items-start gap-3 px-6 py-3', disabled && 'opacity-55')}>
        {content}
      </div>
    )
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-3 px-6 py-3 text-left hover:bg-inverse/5 focus-visible:ring-2 focus-visible:ring-highlight focus-visible:outline-none"
    >
      {content}
    </button>
  )
}

/**
 * Rail navigation entry. A row with no handler is where you already are, so it renders
 * as a labelled location rather than a focusable control that does nothing.
 */
function NavRow({
  label,
  active,
  icon,
  onClick,
}: {
  label: string
  active?: boolean
  icon: React.ReactNode
  onClick?: () => void
}) {
  const shell = cn(
    'mx-3 flex w-[calc(100%-1.5rem)] items-center gap-3 rounded-card px-3 py-2.5 text-left',
    active ? 'bg-sidebar-raised' : 'hover:bg-inverse/5',
  )
  const content = (
    <>
      <span
        className={cn(
          'grid h-7 w-7 shrink-0 place-items-center rounded-full',
          active ? 'bg-highlight text-sidebar' : 'bg-inverse/12 text-inverse/80',
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          'font-jakarta text-[15px]',
          active ? 'font-semibold text-highlight' : 'text-inverse',
        )}
      >
        {label}
      </span>
    </>
  )

  if (!onClick) {
    return (
      <div className={shell} aria-current={active ? 'page' : undefined}>
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        shell,
        'focus-visible:ring-2 focus-visible:ring-highlight focus-visible:outline-none',
      )}
    >
      {content}
    </button>
  )
}

export interface AssessmentSidebarProps {
  overview: Pick<AssessmentOverview, 'prerequisites' | 'requiredSections' | 'optionalSections'>
  onOpenSection?: (sectionId: string) => void
  onOpenPrerequisite?: (gate: PrerequisiteGate) => void
  onBackToList?: () => void
  onOpenGuidance?: () => void
  /** The Guidance page is open, so its entry is the current location. */
  activeGuidance?: boolean
  /**
   * The section (or prerequisite's section) currently open, so its rail entry renders
   * as the current location instead of a control. Unset on the overview itself, where
   * "Overview" is the current location.
   */
  activeSectionId?: string
  /** A prerequisite with its own screen (no backing section) that is currently open. */
  activePrerequisiteKind?: PrerequisiteGate['kind']
  /** Return to the overview from within a section or prerequisite. Only used when one is active. */
  onOpenOverview?: () => void
}

/**
 * Dark rail listing every section of the assessment with its completion mark, so a
 * respondent can see the whole shape of the work and jump straight to any part of it.
 */
export function AssessmentSidebar({
  overview,
  onOpenSection,
  onOpenPrerequisite,
  onBackToList,
  onOpenGuidance,
  activeGuidance = false,
  activeSectionId,
  activePrerequisiteKind,
  onOpenOverview,
}: AssessmentSidebarProps) {
  const { prerequisites, requiredSections, optionalSections } = overview
  const onOverview = !activeSectionId && !activePrerequisiteKind && !activeGuidance

  const sectionEntry = (s: OverviewSection) => (
    <EntryRow
      key={s.id}
      title={s.title}
      mark={s.state}
      disabled={!s.enterable}
      active={s.id === activeSectionId}
      onClick={onOpenSection && s.enterable ? () => onOpenSection(s.id) : undefined}
    />
  )

  return (
    <nav
      aria-label="Assessment sections"
      className="hidden w-[300px] shrink-0 flex-col bg-sidebar pb-10 lg:flex"
    >
      {onBackToList && (
        <button
          type="button"
          onClick={onBackToList}
          className="sticky top-0 z-10 flex items-center gap-2 bg-sidebar px-6 pt-7 pb-5 text-[15px] font-semibold text-inverse hover:underline focus-visible:ring-2 focus-visible:ring-highlight focus-visible:outline-none"
        >
          <ArrowLeftIcon />
          All Assessments
        </button>
      )}

      <NavRow
        label="Overview"
        active={onOverview}
        icon={<LayoutIcon />}
        onClick={onOverview ? undefined : onOpenOverview}
      />
      {(activeGuidance || onOpenGuidance) && (
        <NavRow
          label="Guidance"
          active={activeGuidance}
          icon={<FolderIcon />}
          onClick={activeGuidance ? undefined : onOpenGuidance}
        />
      )}

      {prerequisites.length > 0 && (
        <>
          <GroupLabel>Prerequisites</GroupLabel>
          {prerequisites.map((g) => (
            <EntryRow
              key={g.kind}
              title={g.title}
              mark={
                g.complete ? SectionCompletionState.Complete : SectionCompletionState.NotStarted
              }
              disabled={!g.enterable}
              active={
                g.sectionId !== null
                  ? g.sectionId === activeSectionId
                  : g.kind === activePrerequisiteKind
              }
              onClick={onOpenPrerequisite && g.enterable ? () => onOpenPrerequisite(g) : undefined}
            />
          ))}
        </>
      )}

      {requiredSections.length > 0 && (
        <>
          <GroupLabel>Required sections</GroupLabel>
          {requiredSections.map(sectionEntry)}
        </>
      )}

      {optionalSections.length > 0 && (
        <>
          <GroupLabel>Optional sections</GroupLabel>
          {optionalSections.map(sectionEntry)}
        </>
      )}
    </nav>
  )
}
